import { Link, useNavigate } from '@tanstack/react-router';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDuration } from '../sla/duration';
import { Icon } from '../../components/Icon';
import { RichText } from '../../components/RichText';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingIndicator } from '../../components/Spinner';
import { Tabs } from '../../components/Tabs';
import { PageHeader } from '../../components/PageHeader';
import { MetadataGrid } from '../../components/MetadataGrid';
import { formatDateTime } from '../../i18n';
import { useSession } from '../auth/queries';
import { useCustomFields } from '../customfields/queries';
import { useMasterData } from '../masterdata/queries';
import { useArticleFromTicket } from '../knowledge/queries';
import { MergeSplitDialog } from './MergeSplitDialog';
import { MacroMenu } from '../shortcuts/MacroMenu';
import { BackupTicketPanel } from '../backup/BackupTicketPanel';
import { TimePanel } from '../timetracking/TimePanel';
import { AppointmentPanel } from '../appointments/AppointmentPanel';
import { TicketSlaCard } from '../sla/TicketSlaCard';
import { nameOf, useAssignableUsers } from '../users/queries';
import { ApprovalPanel } from './approvals/ApprovalPanel';
import { AttachmentsPanel } from './AttachmentsPanel';
import { ArticleTimeline } from './articles/ArticleTimeline';
import { type TicketAction } from './api';
import { HistoryPanel } from './HistoryPanel';
import { toneForPriorityLevel, toneForStateCategory } from './presentation';
import { StructurePanel } from './structure/StructurePanel';
import { requestCompose } from './compose';
import { ShortcutHelp } from './ShortcutHelp';
import { TicketFieldsForm } from './TicketFieldsForm';
import { TicketQuickEdit } from './TicketQuickEdit';
import { useTicketShortcuts } from './useTicketShortcuts';
import {
  useTicket,
  useTicketAction,
  useTicketLock,
  useTicketPdf,
  useTicketTabCounts,
  useSetTicketTags,
  useSetTicketWatching,
} from './queries';
import { MultiCombobox } from '../../components/MultiCombobox';

interface TicketDetailPageProps {
  ticketId: string;
}

/**
 * Die Einzelansicht eines Tickets.
 *
 * Der Aufbau folgt der Arbeitsweise: links der Verlauf, an dem gearbeitet
 * wird, rechts die Kennzahlen, die man im Blick behaelt. Die Reiter trennen,
 * was selten gebraucht wird -- Felder, Struktur, Verlauf --, ohne es zu
 * verstecken.
 *
 * Die Zustandsaktionen entsprechen den Endpunkten unter `/actions/`. Welche
 * davon moeglich ist, entscheidet der Server -- die Oberflaeche blendet nur
 * aus, was aus dem aktuellen Zustand offensichtlich nicht in Frage kommt.
 * Die Ausblendung ist Bequemlichkeit, keine Absicherung: Ein Aufruf, den die
 * Oberflaeche nicht anbietet, wird trotzdem serverseitig geprueft --
 * Fachregeln leben im Backend (docs/01, Abschnitt 3).
 */
export function TicketDetailPage({ ticketId }: TicketDetailPageProps) {
  const { t, i18n } = useTranslation();
  const query = useTicket(ticketId);
  // Die Blasen an den Reitern -- F1-55. Ohne Antwort bleibt jede Blase weg,
  // statt eine Null zu zeigen, die niemand gezaehlt hat.
  const counts = useTicketTabCounts(ticketId).data;
  const action = useTicketAction(ticketId);
  const customFields = useCustomFields('ticket');
  const session = useSession();
  const tags = useMasterData('tag');
  const users = useAssignableUsers();
  const setTags = useSetTicketTags(ticketId);
  const setWatching = useSetTicketWatching(ticketId);
  const pdf = useTicketPdf(ticketId);
  const fromTicket = useArticleFromTicket();
  const navigate = useNavigate();
  // Nur wegen der Wirkung: holt die weiche Sperre und gibt sie beim
  // Verlassen zurueck. Angezeigt wird die Sperre aus dem Ticket selbst.
  useTicketLock(ticketId, query.isSuccess);

  const [includeInternal, setIncludeInternal] = useState(false);
  const [tab, setTab] = useState('conversation');
  const [helpOpen, setHelpOpen] = useState(false);

  // Die Kuerzel der Maske -- welche Taste welche ist, steht in den
  // Sprachdateien und in der Uebersicht hinter "?".
  useTicketShortcuts({
    onReply: () => {
      setTab('conversation');
      requestCompose('outgoing_email');
    },
    onNote: () => {
      setTab('conversation');
      requestCompose('note');
    },
    onTime: () => {
      setTab('time');
    },
    onHelp: () => {
      setHelpOpen(true);
    },
  });

  const permissions = session.data?.permissions ?? [];
  const canWrite = permissions.includes('ticket.update');
  const canComment = permissions.includes('ticket.comment');
  const canTransition = permissions.includes('ticket.transition');

  if (query.isPending) return <LoadingIndicator />;
  if (query.isError) return <ErrorNotice error={query.error} />;

  const ticket = query.data;
  const isTerminal =
    ticket.state.state_category === 'resolved' || ticket.state.state_category === 'closed';

  const available: TicketAction[] = isTerminal
    ? ticket.state.state_category === 'closed'
      ? ['reopen']
      : ['close', 'reopen']
    : ['resolve', 'close'];

  // Die Uebernahme steht vor den Zustandsknoepfen und nicht zwischen ihnen:
  // Sie ist der Anfang der Arbeit, nicht ihr Abschluss. Ausgeblendet wird sie
  // nur, wenn sie nichts mehr aendern wuerde -- das Ticket gehoert bereits
  // dem Betrachter und laeuft. Die Ausblendung ist Bequemlichkeit; ob die
  // Aktion zulaessig ist, entscheidet der Server.
  const isOwner = ticket.owner_id === session.data?.user_id;
  const canClaim =
    canWrite && canTransition && !(isOwner && ticket.state.state_category === 'open');

  // Die Sperre steht am Ticket selbst. Sie aus dem Fehlercode der eigenen
  // Anforderung abzuleiten waere ungenau: Ein 409 kann auch entstehen, wenn
  // die eigene Sperre aus einem zweiten Reiter noch laeuft (F1-24, K-21).
  // Die Sperre haelt niemanden ab -- sie erspart nur, dass zwei Leute erst
  // nach zehn Minuten Arbeit voneinander erfahren.
  const lockedByOther =
    ticket.lock !== null &&
    ticket.lock !== undefined &&
    ticket.lock.user_id !== session.data?.user_id;

  const isWatching = (ticket.watcher_ids ?? []).includes(session.data?.user_id ?? '');

  return (
    <>
      <PageHeader
        breadcrumb={[t('tickets.title'), ticket.queue.name]}
        title={ticket.title}
        subtitle={ticket.ticket_number}
        action={
          <div className="row" role="group" aria-label={t('tickets.actions')}>
            {canWrite ? <MacroMenu ticketId={ticket.id} /> : null}
            {canWrite ? <MergeSplitDialog ticketId={ticket.id} /> : null}

            <Button
              variant="ghost"
              busy={pdf.isPending}
              onClick={() => {
                pdf.mutate({ includeInternal, fallbackName: `${ticket.ticket_number}.pdf` });
              }}
            >
              {t('tickets.downloadPdf')}
            </Button>

            {/* Wissen aus dem geloesten Vorgang -- F4-12, K-108. Erst wenn er
                  geloest ist: Vorher ist der Verlauf noch keine Loesung. */}
            {canWrite && ticket.resolved_at ? (
              <Button
                variant="ghost"
                busy={fromTicket.isPending}
                onClick={() => {
                  fromTicket.mutate(
                    { ticket_id: ticketId },
                    {
                      onSuccess: () => {
                        void navigate({ to: '/app/knowledge' });
                      },
                    },
                  );
                }}
              >
                {t('knowledge.fromTicket')}
              </Button>
            ) : null}

            {canClaim ? (
              <Button
                variant="primary"
                busy={action.isPending && action.variables === 'claim'}
                onClick={() => {
                  action.mutate('claim');
                }}
              >
                {t('tickets.claim')}
              </Button>
            ) : null}

            {available.map((name) => (
              <Button
                key={name}
                variant={name === 'close' && !canClaim ? 'primary' : 'secondary'}
                busy={action.isPending && action.variables === name}
                onClick={() => {
                  action.mutate(name);
                }}
              >
                {t(`tickets.${name}`)}
              </Button>
            ))}
          </div>
        }
      />

      {/* Kennzahlen unter dem Betreff -- Queue, Prioritaet, Besitzer, Angelegt.
          Der Status steht in der Seitenleiste; hier die uebrigen Kennzahlen. */}
      <MetadataGrid
        entries={[
          { label: t('tickets.queue'), value: ticket.queue.name },
          {
            label: t('tickets.priority'),
            value: (
              <Badge tone={toneForPriorityLevel(ticket.priority.level)}>
                {ticket.priority.name}
              </Badge>
            ),
          },
          {
            label: t('tickets.owner'),
            value: nameOf(users.data, ticket.owner_id) ?? t('tickets.unassigned'),
          },
          {
            label: t('tickets.createdAt'),
            value: formatDateTime(ticket.created_at, i18n.language),
          },
        ]}
      />

      {/* Prioritaet, Bearbeiter und Warteschlange direkt am Ticketkopf --
            F1-42. Die Schnellzeile bleibt erhalten; die Kennzahlen oben sind
            die Uebersicht, hier wird geaendert. */}
      <TicketQuickEdit ticket={ticket} canWrite={canWrite} />

      {ticket.customer.is_vip ? (
        <div className="notice notice--vip" role="status">
          {t('crm.vipNotice', { name: ticket.customer.name })}
        </div>
      ) : null}

      {lockedByOther ? (
        <div className="notice notice--warning" role="status">
          {t('tickets.lockedByOther')}
        </div>
      ) : null}

      {action.isError ? <ErrorNotice error={action.error} /> : null}
      {pdf.isError ? <ErrorNotice error={pdf.error} /> : null}

      <div className="detail-grid">
        <div className="stack">
          <Tabs
            ariaLabel={t('tickets.sections')}
            value={tab}
            onValueChange={setTab}
            tabs={[
              {
                value: 'conversation',
                count: counts?.conversation ?? undefined,
                label: t('tickets.conversation'),
                content: (
                  <>
                    <section className="card">
                      <h2 style={{ marginBottom: 'var(--space-4)' }}>{t('tickets.description')}</h2>
                      {/* Zwei Wege, und das ist Absicht -- F1-10.
                          Mit Auszeichnung: `RichText` baut Elemente aus dem
                          bereinigten HTML. Ohne: der Klartext mit seinen
                          Zeilenumbruechen, wie bisher. Ein Vorgang aus dem
                          Portal oder aus der Zeit vor dem Editor traegt kein
                          HTML -- ihn durch den Auszeichnungsweg zu schicken
                          liesse seine Zeilenumbrueche fallen, denn in HTML
                          gelten sie nicht. */}
                      {ticket.description_html ? (
                        <RichText html={ticket.description_html} />
                      ) : (
                        <p className="prose">{ticket.description || t('app.none')}</p>
                      )}
                    </section>

                    {/* F1-50. Ueber dem Schreibfeld und nicht im Kopf: Hier
                        entsteht die Antwort, die sie wieder verschwinden
                        laesst -- und wer sie liest, hat die Hand schon am
                        richtigen Ort. */}
                    {ticket.awaiting_reply_since ? (
                      <div className="waiting-bar">
                        <span className="waiting-bar__text">
                          <Icon name="mailIn" />
                          {t('tickets.awaitingSince', {
                            when: formatDuration(
                              (Date.now() - Date.parse(ticket.awaiting_reply_since)) / 1000,
                              t,
                            ),
                          })}
                        </span>
                        {ticket.unanswered_count > 0 ? (
                          <span className="waiting-chip__count">{ticket.unanswered_count}</span>
                        ) : null}
                        <span className="shell__spacer" />
                        {canComment ? (
                          <Button
                            variant="ghost"
                            title={t('tickets.dismissWaitingHint')}
                            busy={action.isPending && action.variables === 'dismiss-waiting'}
                            onClick={() => {
                              action.mutate('dismiss-waiting');
                            }}
                          >
                            {t('tickets.dismissWaiting')}
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                    <ArticleTimeline
                      ticketId={ticketId}
                      canWrite={canComment}
                      descriptionHtml={ticket.description_html}
                      awaitingSince={ticket.awaiting_reply_since}
                    />
                  </>
                ),
              },
              {
                value: 'attachments',
                count: counts?.attachments ?? undefined,
                label: t('attachments.title'),
                content: <AttachmentsPanel ticketId={ticketId} canWrite={canWrite} />,
              },
              {
                value: 'fields',
                label: t('tickets.fields'),
                content: (
                  <section className="card">
                    {canWrite ? (
                      <TicketFieldsForm ticket={ticket} />
                    ) : (
                      <p className="table__muted">{t('tickets.readOnly')}</p>
                    )}

                    {/* Im Schema wahlfrei, weil es eine Vorgabe hat -- ein
                        Ticket ohne eigene Felder liefert ein leeres Objekt. */}
                    {Object.keys(ticket.custom_fields ?? {}).length > 0 ? (
                      <>
                        <h2 style={{ margin: 'var(--space-4) 0' }}>{t('tickets.customFields')}</h2>
                        <dl className="definition-list">
                          {customFields.data
                            ?.filter(
                              (definition) => ticket.custom_fields?.[definition.key] !== undefined,
                            )
                            .map((definition) => (
                              <Fragment key={definition.id}>
                                <dt>{definition.label}</dt>
                                <dd>{formatCustomValue(ticket.custom_fields?.[definition.key])}</dd>
                              </Fragment>
                            ))}
                        </dl>
                      </>
                    ) : null}
                  </section>
                ),
              },
              {
                value: 'appointments',
                count: counts?.appointments ?? undefined,
                label: t('appointments.title'),
                content: <AppointmentPanel ticketId={ticketId} canWrite={canWrite} />,
              },
              {
                value: 'time',
                count: counts?.time_entries ?? undefined,
                label: t('time.title'),
                content: <TimePanel ticketId={ticketId} canWrite={canWrite} />,
              },
              {
                value: 'structure',
                count: counts?.structure ?? undefined,
                label: t('tickets.structure'),
                content: <StructurePanel ticketId={ticketId} canWrite={canWrite} />,
              },
              {
                value: 'approvals',
                count: counts?.approvals ?? undefined,
                label: t('approvals.title'),
                content: <ApprovalPanel ticketId={ticketId} canRequest={canWrite} />,
              },
              {
                value: 'history',
                label: t('tickets.history'),
                content: <HistoryPanel ticketId={ticketId} active />,
              },
            ]}
          />
        </div>

        <aside className="card">
          {/* Der Kasten am Backup-Ticket -- F5-01, Anforderung 3. Er zeigt
              sich nur, wenn das Ticket eines ist; bei jedem anderen Vorgang
              steht hier nichts. Ganz oben, weil er die Frage beantwortet,
              mit der man ein Backup-Ticket oeffnet: steht das Geraet immer
              noch schlecht da, und laesst es sich nochmal versuchen. */}
          <BackupTicketPanel
            ticketId={ticketId}
            canManage={permissions.includes('backup.manage')}
          />

          <div className="row" style={{ marginBottom: 'var(--space-4)' }}>
            <Button
              variant={isWatching ? 'secondary' : 'ghost'}
              aria-pressed={isWatching}
              busy={setWatching.isPending}
              onClick={() => {
                setWatching.mutate(!isWatching);
              }}
            >
              {isWatching ? t('tickets.unwatch') : t('tickets.watch')}
            </Button>
          </div>

          <dl className="definition-list">
            <dt>{t('crm.customer')}</dt>
            <dd>
              <Link to="/app/crm/$customerId" params={{ customerId: ticket.customer.id }}>
                {ticket.customer.name}
              </Link>
              {ticket.customer.is_vip ? (
                <>
                  {' '}
                  <Badge tone="warning" dot>
                    {t('crm.vip')}
                  </Badge>
                </>
              ) : null}
            </dd>

            <dt>{t('crm.contact')}</dt>
            <dd>
              {ticket.contact ? (
                `${ticket.contact.first_name} ${ticket.contact.last_name}`.trim()
              ) : (
                <span className="table__muted">{t('crm.noContact')}</span>
              )}
            </dd>

            <dt>{t('tickets.state')}</dt>
            <dd>
              <Badge tone={toneForStateCategory(ticket.state.state_category)} dot>
                {ticket.state.name}
              </Badge>
            </dd>

            <dt>{t('tickets.priority')}</dt>
            <dd>
              <Badge tone={toneForPriorityLevel(ticket.priority.level)}>
                {ticket.priority.name}
              </Badge>
            </dd>

            <dt>{t('tickets.type')}</dt>
            <dd>{ticket.type.name}</dd>

            <dt>{t('tickets.category')}</dt>
            <dd>
              {ticket.category ? (
                ticket.category.name
              ) : (
                <span className="table__muted">{t('tickets.noCategory')}</span>
              )}
            </dd>

            {ticket.impact ? (
              <>
                <dt>{t('tickets.impact')}</dt>
                <dd>{ticket.impact.name}</dd>
              </>
            ) : null}

            {ticket.urgency ? (
              <>
                <dt>{t('tickets.urgency')}</dt>
                <dd>{ticket.urgency.name}</dd>
              </>
            ) : null}

            <dt>{t('tickets.queue')}</dt>
            <dd>{ticket.queue.name}</dd>

            <dt>{t('tickets.owner')}</dt>
            <dd>{nameOf(users.data, ticket.owner_id) ?? t('tickets.unassigned')}</dd>

            <dt>{t('tickets.responsible')}</dt>
            <dd>{nameOf(users.data, ticket.responsible_id) ?? t('tickets.unassigned')}</dd>

            <dt>{t('tickets.source')}</dt>
            <dd>{t(`ticketSource.${ticket.source}`)}</dd>

            <dt>{t('tickets.createdAt')}</dt>
            <dd>{formatDateTime(ticket.created_at, i18n.language)}</dd>

            <dt>{t('tickets.updatedAt')}</dt>
            <dd>{formatDateTime(ticket.updated_at, i18n.language)}</dd>

            {ticket.resolved_at ? (
              <>
                <dt>{t('tickets.resolvedAt')}</dt>
                <dd>{formatDateTime(ticket.resolved_at, i18n.language)}</dd>
              </>
            ) : null}

            {ticket.closed_at ? (
              <>
                <dt>{t('tickets.closedAt')}</dt>
                <dd>{formatDateTime(ticket.closed_at, i18n.language)}</dd>
              </>
            ) : null}
          </dl>

          {/* -- Die SLA-Uhr (F4-08, K-118) -------------------------------- */}
          <TicketSlaCard ticketId={ticketId} sla={ticket.sla ?? null} />

          {/* -- Tags (F1-21, K-25) ---------------------------------------- */}
          <h2 style={{ margin: 'var(--space-4) 0 var(--space-2)' }}>{t('tickets.tags')}</h2>
          {/* Als Marken mit Suchfeld und nicht als Reihe von Knoepfen: Die
              Reihe zeigte jeden Tag des Mandanten, auch die neunundzwanzig,
              die an diesem Ticket nichts zu suchen haben (F1-49). */}
          <MultiCombobox
            ariaLabel={t('tickets.tags')}
            value={ticket.tag_ids ?? []}
            disabled={!canWrite || setTags.isPending}
            placeholder={t('tickets.addTag')}
            options={(tags.data ?? [])
              .filter((tag) => tag.is_active !== false || (ticket.tag_ids ?? []).includes(tag.id))
              .map((tag) => ({ id: tag.id, label: tag.name }))}
            onChange={(next) => {
              setTags.mutate(next);
            }}
          />

          {setTags.isError ? <ErrorNotice error={setTags.error} /> : null}

          <label className="field__checkbox" style={{ marginTop: 'var(--space-4)' }}>
            <input
              type="checkbox"
              checked={includeInternal}
              onChange={(event) => {
                setIncludeInternal(event.target.checked);
              }}
            />
            {t('tickets.pdfIncludeInternal')}
          </label>
        </aside>
      </div>

      <ShortcutHelp open={helpOpen} onOpenChange={setHelpOpen} />
    </>
  );
}

/**
 * Stellt den Wert eines eigenen Feldes dar.
 *
 * Ohne Wissen ueber den Feldtyp -- die Beschriftung kommt aus der Festlegung,
 * der Wert steht so da, wie er gespeichert wurde. Eine Mehrfachauswahl wird
 * zur Aufzaehlung, ein Wahrheitswert zu ja oder nein.
 */
function formatCustomValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
  if (Array.isArray(value)) return value.map(formatCustomValue).join(', ');
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  // Ein Wert, den die Oberflaeche nicht kennt, bleibt leer statt als
  // "[object Object]" zu erscheinen.
  return '—';
}
